import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError, unwrap } from '@/api/envelope';
import { inviteOrganizationSuperAdmin } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { useToast } from '@/components/ui/useToast';

const inviteSchema = z.object({
  full_name: z.string().trim().min(1, 'Enter their name'),
  email: z
    .string()
    .min(1, 'Enter their email address')
    .email('Enter a valid email address'),
});

type InviteValues = z.infer<typeof inviteSchema>;

interface InviteSuperAdminFormProps {
  organizationId: string;
  /** Reload the list above, so the new invitation appears in it. */
  onInvited: () => void;
}

/**
 * Invite somebody to be this organization's Super Admin.
 *
 * A short form at the foot of the card rather than a dialog: two fields, on a
 * card staff are already looking at, and inviting a second Super Admin should
 * not feel like a bigger act than inviting the first.
 *
 * The role is not a field. Staff invite Super Admins and nothing else - the
 * backend forces it whatever the request says - so offering a choice here
 * would be offering one that is ignored.
 */
export function InviteSuperAdminForm({
  organizationId,
  onInvited,
}: InviteSuperAdminFormProps) {
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { full_name: '', email: '' },
  });

  // Announced by moving focus to it, so somebody using a screen reader hears
  // the failure instead of having to go looking for red text.
  useEffect(() => {
    if (!formError) return;
    alertRef.current?.focus();
  }, [formError]);

  const invite = useMutation({
    mutationFn: async (values: InviteValues) => {
      const reply = await inviteOrganizationSuperAdmin({
        path: { organization_id: organizationId },
        body: { full_name: values.full_name.trim(), email: values.email.trim() },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFormError(null),
    onSuccess: (data) => {
      reset({ full_name: '', email: '' });
      onInvited();
      // The backend says whether the email actually went. An invitation that
      // exists but was never emailed is the one case where "sent" would be a
      // lie, and the fix is a resend rather than a second invitation.
      if (data?.email_sent === false) {
        toast.show({
          message:
            'The invitation was created but the email could not be sent. Resend it.',
          severity: 'error',
        });
        return;
      }
      toast.show({ message: 'Invitation sent.' });
    },
    onError: (error) => {
      // A conflict is about the address, so it belongs on the address field
      // rather than floating above the form. The backend's own wording is
      // used: it knows whether the clash is a staff account or an existing
      // member, and those need different fixes.
      if (error instanceof ApiError && error.status === 409) {
        setError('email', {
          message: error.serverMessage ?? 'That address already has a portal account.',
        });
        return;
      }
      setFormError(
        (error instanceof ApiError ? error.serverMessage : null) ??
          'We could not send that invitation. Please try again.',
      );
    },
  });

  return (
    <Stack
      component="form"
      spacing={2}
      // Named, so its fields can be told apart from the account page's own
      // contact fields, which also have an "Email address". A form with an
      // accessible name is a landmark a test - and a screen reader user -
      // can scope to.
      aria-label="Invite a Super Admin"
      // noValidate so the browser's own bubbles do not pre-empt the messages
      // this form attaches to each field.
      noValidate
      onSubmit={(event) => {
        void handleSubmit((values) => invite.mutate(values))(event);
      }}
      sx={{ mt: 1, maxWidth: 480 }}
    >
      {/* One level under the Super Admins heading, never two. */}
      <Typography variant="subtitle2" component="h3">
        Invite a Super Admin
      </Typography>

      {formError ? (
        <Alert severity="error" ref={alertRef} tabIndex={-1} role="alert">
          {formError}
        </Alert>
      ) : null}

      <Field label="Full name" error={errors.full_name?.message}>
        <TextField {...register('full_name')} size="small" fullWidth />
      </Field>

      <Field label="Email address" error={errors.email?.message}>
        <TextField {...register('email')} type="email" size="small" fullWidth />
      </Field>

      {/* Sized to its label rather than stretched across the card, which is
          how every other action in the portal is drawn. */}
      <Button
        type="submit"
        variant="primary"
        loading={invite.isPending}
        sx={{ alignSelf: 'flex-start' }}
      >
        Send invitation
      </Button>
    </Stack>
  );
}
